/**
 * Glosario ALINEA — términos del sistema, perfiles y biometría.
 * Fuente única para tooltips (ℹ) y página /admin/glosario.
 */
import { ALINEA_PROFILES, FEELINGS, FEELING_REASONS } from '../utils/constants.js';
import { HD_TYPES } from '../utils/enneagramHd.js';

const CATEGORY_ORDER = [
    'Perfiles ALINEA',
    'Cultura y manada',
    'Jornada y bienestar',
    'Diagnóstico y encuestas',
    'Gestión e inteligencia',
    'Bienestar y biometría',
];

const BASE_GLOSSARY = [
    {
        id: 'manada',
        match: ['manada', 'mapa de manada'],
        title: 'Manada',
        short: 'Metáfora de equipo: personas que comparten turno, área y ritmo operativo.',
        definition:
            'En ALINEA no es jerarquía militar: es el conjunto humano que sostiene la operación. El registro de manada y el panel del supervisor leen señales de la manada sin exponer confesiones íntimas.',
        category: 'Cultura y manada',
    },
    {
        id: 'jornada',
        match: ['jornada', 'mi jornada'],
        title: 'Jornada',
        short: 'Pantalla diaria del operario: llegada, estado, EPA y mensajes.',
        definition:
            'Centro de presencia operativa. Aquí se registra cómo llegas, opcionalmente biometría, pausas EPA y cierre. No sustituye nómina ni control de asistencia legal por sí sola.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'check_in',
        match: ['check-in', 'check in', 'registra tu llegada', 'llegada'],
        title: 'Registro de llegada (check-in)',
        short: 'Toque rápido al iniciar turno: Bien, Cansado, Saturado o Tenso.',
        definition:
            'Alimenta energía del equipo para el supervisor y mensajes del día. Es autodeclaración, no veredicto del sistema sobre la persona.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'en_eje',
        match: ['en eje', 'En Eje'],
        title: 'En eje',
        short: 'Estado declarado favorable: presencia alineada con el ritmo.',
        definition:
            'Etiqueta de bienestar en encuestas y jornada (equivalente técnico: feeling «bien»). No implica evaluación de desempeño.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'en_sombra',
        match: ['en sombra', 'En Sombra'],
        title: 'En sombra',
        short: 'Estado de mayor tensión o desgaste percibido.',
        definition:
            'Invita apoyos y calibración (feeling «mal»). El supervisor ve señal agregada, no un «castigo» automático.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'registro_manada',
        match: ['registro de manada', 'registro manada', 'diagnóstico de manada'],
        title: 'Registro de manada',
        short: 'Encuesta oficial ALINEA (10 preguntas) sobre estilo de trabajo.',
        definition:
            'Misma plantilla para todas las empresas. Operario y supervisor la completan al entrar y ~cada 3 meses. Calcula perfil ALINEA (Constante, Impulsivo, Analítico, Social) sin calificar moralmente a la persona.',
        category: 'Diagnóstico y encuestas',
    },
    {
        id: 'perfil_alinea',
        match: ['perfil ALINEA', 'perfil maestro', 'perfil de manada'],
        title: 'Perfil ALINEA',
        short: 'Resultado del registro de manada: preferencia de ritmo, foco y vínculo en el trabajo.',
        definition:
            'Cuatro perfiles maestros (Constante, Impulsivo, Analítico, Social), alineados a cuatro ontologías (Generación, Manifestación, Proyección, Reflejo). Sirve para mensajes y coach, no para sanción.',
        category: 'Perfiles ALINEA',
    },
    {
        id: 'observador',
        match: ['observador', 'Observador'],
        title: 'Observador',
        short: 'Formulario breve de reflexión operativa (metadatos, no confesionario).',
        definition:
            'Captura señales léxicas y contexto para fricción sistémica y alertas. No almacena video; no es evaluación psicológica clínica.',
        category: 'Diagnóstico y encuestas',
    },
    {
        id: 'alertas',
        match: ['alertas', 'alerta operativa'],
        title: 'Alertas',
        short: 'Señales preventivas para el supervisor según reglas del sistema.',
        definition:
            'Combinan jornada, encuestas y patrones (p. ej. frases dominantes). Requieren actuación humana del encargado; no son despido automático.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'apoyos',
        match: ['apoyos', 'hub de apoyos'],
        title: 'Apoyos',
        short: 'Acceso del operario/supervisor a perfil, EPA y recursos de autocuidado.',
        definition:
            'Menú de acompañamiento: renovar registro de manada, EPA, cuenta y enlaces útiles según rol.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'epa',
        match: ['EPA', 'Escucha · Procesamiento · Ajuste', 'escucha, procesamiento y ajuste'],
        title: 'EPA',
        short: 'Escucha · Procesamiento · Ajuste — ritual de regulación en jornada.',
        definition:
            'Nombrar sensación y necesidad, micro-pausa o ajuste. Espacio para soltar carga sin convertir el momento en conflicto laboral.',
        category: 'Jornada y bienestar',
    },
    {
        id: 'plan_coach',
        match: ['plan de coach', 'coach de manada'],
        title: 'Plan de coach',
        short: 'Guía trimestral para el supervisor según perfiles del equipo.',
        definition:
            'Temario, preguntas del banco maestro y seguimiento de manada. Herramienta de conversación, no auditoría punitiva.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'banco_preguntas',
        match: ['banco de preguntas', 'preguntas poderosas'],
        title: 'Banco de preguntas poderosas',
        short: 'Preguntas sugeridas para diálogo con el equipo (empatía, no interrogatorio).',
        definition:
            'Organizadas por tema (escuchar, error, adaptarse, propósito). El supervisor las usa en campo; el admin puede editar el banco maestro.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'mapa_friccion',
        match: ['mapa de fricción', 'mapa de friccion', 'fricción sistémica'],
        title: 'Mapa de fricción sistémica',
        short: 'Vista admin de patrones de energía, carga y maestría por área.',
        definition:
            'Inteligencia preventiva agregada. Lee dominios operativos, no culpabiliza personas por nombre en la demo comercial.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'cid',
        match: ['CID', 'Calculadora CID', 'costo de inestabilidad'],
        title: 'Calculadora CID',
        short: 'Estimación económica de inestabilidad, rotación y fricción por área.',
        definition:
            'Pilar de gestión ALINEA: maestros de costo, simulación y dashboard anual para priorizar inversión en estabilidad.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'maestria',
        match: ['maestría técnica', 'maestria tecnica', 'labrar tu maestría'],
        title: 'Maestría técnica',
        short: 'Excelencia sostenida en el gesto de trabajo con sentido y ritmo propio.',
        definition:
            'Marco cultural de ALINEA: el perfil describe cómo prefieres trabajar, no quién «vale más» en la manada.',
        category: 'Cultura y manada',
    },
    {
        id: 'energia_equipo',
        match: ['energía del equipo', 'energia del equipo'],
        title: 'Energía del equipo',
        short: 'Resumen de estados recientes de llegada en la manada.',
        definition:
            'Vista supervisor: proporción en eje, esfuerzo o sombra según últimos check-in. No es historial completo de cada persona.',
        category: 'Gestión e inteligencia',
    },
    {
        id: 'carga_alostatica',
        match: ['carga alostática', 'carga alostatica'],
        title: 'Carga alostática',
        short: 'Desgaste acumulado por adaptarse al estrés sostenido.',
        definition:
            'Concepto de Bruce McEwen usado en ALINEA cuando biometría o tensión somática sugieren sobrecarga. Invita micro-pausas y gesto técnico consciente; no es diagnóstico médico ni castigo.',
        category: 'Bienestar y biometría',
    },
    {
        id: 'biometria',
        match: ['biometría', 'biometria', 'escaneo biométrico', 'escaneo facial', 'sintonía biométrica'],
        title: 'Biometría (escaneo)',
        short: 'Lectura orientativa con cámara: ritmo y tensión perceptible.',
        definition:
            'Opcional y con consentimiento. Apoya calibración del día; no guarda video en servidor ni evalúa desempeño laboral.',
        category: 'Bienestar y biometría',
    },
    {
        id: 'decreto_calibracion',
        match: ['decreto de calibración', 'decreto de calibracion', 'calibración'],
        title: 'Decreto de calibración',
        short: 'Mensaje breve de reencuadre tras tensión o inconsistencia.',
        definition:
            'Frase de apoyo (respirar, hombros, siguiente gesto). Orientación operativa, no reporte automático al supervisor.',
        category: 'Bienestar y biometría',
    },
    {
        id: 'tension_somatica',
        match: ['tensión somática', 'tension somatica', 'tensión en el cuerpo'],
        title: 'Tensión somática',
        short: 'Malestar o rigidez percibida en el cuerpo en jornada.',
        definition:
            'Motivo de check-in (p. ej. «Tenso») o lectura biométrica. Señal para autocuidado, no falta de compromiso.',
        category: 'Bienestar y biometría',
    },
    {
        id: 'inconsistencia_biometrica',
        match: ['inconsistencia', 'incoherencia biométrica'],
        title: 'Inconsistencia biométrica',
        short: 'Diferencia entre lo declarado y la señal opcional de cámara.',
        definition:
            'Solo si la empresa activó biometría. Dispara calibración amable; no es «detector de mentiras» en comunicación externa.',
        category: 'Bienestar y biometría',
    },
];

function buildProfileEntries() {
    const entries = [];
    for (const [key, p] of Object.entries(ALINEA_PROFILES)) {
        entries.push({
            id: `perfil_${key}`,
            match: [
                p.name,
                p.archetype,
                p.archetypeShort,
                `El ${key}`,
                key === 'constante' ? 'Constante' : key === 'impulsivo' ? 'Impulsivo' : key === 'analitico' ? 'Analítico' : 'Social',
            ],
            title: p.name,
            short: p.message,
            definition: `${p.description} ${p.inner_path} ${p.ontology}`.replace(/\s+/g, ' ').trim(),
            category: 'Perfiles ALINEA',
        });
    }
    for (const [key, h] of Object.entries(HD_TYPES)) {
        const profileKey = Object.entries(ALINEA_PROFILES).find(([, p]) => p.v2_id === key)?.[0];
        const linked = profileKey ? ALINEA_PROFILES[profileKey].name : h.name;
        entries.push({
            id: `ontologia_${key}`,
            match: [h.name, key],
            title: `Ontología: ${h.name}`,
            short: `Dimensión v2 asociada al perfil ${linked}.`,
            definition: `Cuarta capa del modelo ALINEA (diseño humano operativo). ${h.name} describe cómo se expresa la energía en planta; se cruza con el perfil ${linked} del registro de manada.`,
            category: 'Perfiles ALINEA',
        });
    }
    return entries;
}

function buildFeelingEntries() {
    const out = [];
    for (const [key, f] of Object.entries(FEELINGS)) {
        out.push({
            id: `feeling_${key}`,
            match: [f.label],
            title: f.label,
            short: `Estado de encuesta/jornada (${key}).`,
            definition: `Categoría de bienestar en ALINEA para check-in y encuestas semanales. Valor técnico: «${key}».`,
            category: 'Jornada y bienestar',
        });
    }
    for (const [key, r] of Object.entries(FEELING_REASONS)) {
        out.push({
            id: `feeling_reason_${key}`,
            match: [r.label],
            title: r.label,
            short: 'Motivo somático al declarar esfuerzo o sombra.',
            definition: `Submotivo en encuesta de inicio. Valor técnico: «${key}». Ayuda a personalizar el decreto de calibración.`,
            category: 'Jornada y bienestar',
        });
    }
    return out;
}

export const ALINEA_GLOSSARY = [...BASE_GLOSSARY, ...buildProfileEntries(), ...buildFeelingEntries()];

export function glossaryMatchPatterns() {
    const phrases = [];
    for (const entry of ALINEA_GLOSSARY) {
        for (const m of entry.match) {
            if (m && String(m).trim()) phrases.push({ phrase: m, entry });
        }
    }
    return phrases.sort((a, b) => b.phrase.length - a.phrase.length);
}

export function getGlossaryById(id) {
    return ALINEA_GLOSSARY.find((e) => e.id === id) || null;
}

export function glossaryCategories() {
    const set = new Set(ALINEA_GLOSSARY.map((e) => e.category));
    return CATEGORY_ORDER.filter((c) => set.has(c)).concat([...set].filter((c) => !CATEGORY_ORDER.includes(c)));
}
